'use memo';

import { useQueryClient } from '@tanstack/react-query';
import { AutopilotTaskDto, TaskCategoryTaskType } from '@urbanx/agx.activities.client';
import {
  CampaignDetailModel,
  CampaignDetailModelState,
  CommissionDetails,
  ListingAgreementDetails,
  StaffUpdateAgreementDetailsForCampaignRequest,
  StaffUpdateCommissionInfoForCampaignRequest,
  StaffUpdateCommissionInfoForCampaignRequestChangeType,
  useInvalidateUseStaffGetAgreementDetailsForCampaign,
  useInvalidateUseStaffGetCommissionInfoForCampaign,
  useStaffGetAgreementDetailsForCampaign,
  useStaffGetCommissionInfoForCampaign,
  useStaffGetDocumentsForCampaign,
  useStaffUpdateAgreementDetailsForCampaign,
  useStaffUpdateCommissionInfoForCampaign,
} from '@urbanx/agx.campaigns.client';
import { createCampaignDetailsQueryKey } from 'hooks/useSelectedCampaign';
import { filterDocumentGroups } from 'pages/campaigns/components/campaignTasks/AgreementCompliance/AgreementComplianceHelper';
import { useEffect, useState } from 'react';
import CompliancePages from './CompliancePages';
import { TaskModalPageProps } from './TaskModalPageProps';
import type { TaskModalContentConfig } from './TaskModalDialog';
import { createAgreementComplianceTaskModalFlow } from './agreementComplianceTaskLayout';
import {
  hasExceededAgreementPeriod,
  hasValidNswAgreementDetails,
  hasValidQldAgreementDetails,
  isAgreementTypeOpen,
} from './agreementComplianceValidation';

interface UseAgreementComplianceModalProps {
  campaignId: string;
  campaign: CampaignDetailModel;
  isOpen: boolean;
  tasks: AutopilotTaskDto[];
}

export const useAgreementComplianceModal = ({
  campaignId,
  campaign,
  isOpen,
  tasks,
}: UseAgreementComplianceModalProps) => {
  const queryClient = useQueryClient();
  const documentsEnabled = Boolean(campaign?.campaignId) && isOpen;
  const { data: documentGroups, isPending: documentsPending } = useStaffGetDocumentsForCampaign(
    campaign?.campaignId || '',
    documentsEnabled
  );

  const filteredDocuments =
    documentGroups && Array.isArray(documentGroups) ? filterDocumentGroups(documentGroups) : [];

  const { data: agreementDetailsData, isPending: agreementDetailsPending } =
    useStaffGetAgreementDetailsForCampaign(campaignId, !!campaignId && isOpen);
  const { data: commissionInformation, isPending: commissionInformationPending } =
    useStaffGetCommissionInfoForCampaign(campaignId, !!campaignId && isOpen);

  const currentCommissionInfo =
    commissionInformation?.commissionInformation?.currentCommissionDetails?.commissionDetails;

  const [agreementDetails, setAgreementDetails] = useState<ListingAgreementDetails | undefined>();
  const [agreementDetailsDirty, setAgreementDetailsDirty] = useState(false);
  const [commissionDetails, setCommissionDetails] = useState<CommissionDetails | undefined>();
  const [commissionDetailsDirty, setCommissionDetailsDirty] = useState(false);
  useEffect(() => {
    if (!agreementDetailsDirty) setAgreementDetails(agreementDetailsData);
  }, [agreementDetailsData, agreementDetailsDirty]);

  useEffect(() => {
    if (!commissionDetailsDirty) setCommissionDetails(currentCommissionInfo);
  }, [commissionDetailsDirty, currentCommissionInfo]);

  const invalidateAgreementDetails = useInvalidateUseStaffGetAgreementDetailsForCampaign();
  const invalidateCommissionInfo = useInvalidateUseStaffGetCommissionInfoForCampaign();

  const invalidateCampaignDetails = () => {
    queryClient.invalidateQueries({
      queryKey: createCampaignDetailsQueryKey(campaignId),
    });
  };

  const { staffUpdateAgreementDetailsForCampaign } = useStaffUpdateAgreementDetailsForCampaign({
    onSuccess: async () => {
      await invalidateAgreementDetails(campaignId);
      invalidateCampaignDetails();
    },
    onError: () => {},
  });

  const { staffUpdateCommissionInfoForCampaign } = useStaffUpdateCommissionInfoForCampaign({
    onSuccess: async () => {
      await invalidateCommissionInfo(campaignId);
      invalidateCampaignDetails();
    },
    onError: () => {},
  });

  const onAgreementDetailsUpdated = (updated: { id: string; value: ListingAgreementDetails }) => {
    const hasChanged = JSON.stringify(agreementDetails) !== JSON.stringify(updated.value);

    setAgreementDetails((previous) =>
      JSON.stringify(previous) === JSON.stringify(updated.value) ? previous : updated.value
    );
    if (hasChanged) setAgreementDetailsDirty(true);
  };

  const onCommissionUpdated = (updated: { value: CommissionDetails }) => {
    const hasChanged = JSON.stringify(commissionDetails) !== JSON.stringify(updated.value);

    setCommissionDetails((previous) =>
      JSON.stringify(previous) === JSON.stringify(updated.value) ? previous : updated.value
    );
    if (hasChanged) setCommissionDetailsDirty(true);
  };

  const hasAgreementDetailsTask = tasks.some(
    (task) => task.category?.taskType === TaskCategoryTaskType.AgreementDetails
  );
  const hasAgreementTypeTask = tasks.some(
    (task) => task.category?.taskType === TaskCategoryTaskType.AgreementComplianceType
  );
  const qldAgreementPassed = hasValidQldAgreementDetails(agreementDetails, hasAgreementTypeTask);
  const nswAgreementPassed = hasValidNswAgreementDetails(agreementDetails, hasAgreementDetailsTask);
  const completeDisabled = (() => {
    switch (campaign?.state) {
      case CampaignDetailModelState.QLD:
        return !qldAgreementPassed;
      case CampaignDetailModelState.NSW:
        return !nswAgreementPassed;
      default:
        return false;
    }
  })();

  const saveAgreementDetails = async () => {
    if (!agreementDetailsDirty || !agreementDetails) return;

    await staffUpdateAgreementDetailsForCampaign({
      campaignId,
      agreementDetails,
    } as StaffUpdateAgreementDetailsForCampaignRequest);
    setAgreementDetailsDirty(false);
  };

  const saveCommissionDetails = async () => {
    if (!commissionDetailsDirty || !commissionDetails) return;

    await staffUpdateCommissionInfoForCampaign({
      campaignId,
      commissionDetails,
      changeType: StaffUpdateCommissionInfoForCampaignRequestChangeType.Other,
      reason: 'Agreement Compliance Completion',
    } as StaffUpdateCommissionInfoForCampaignRequest);
    setCommissionDetailsDirty(false);
  };

  const onSaveCurrentPage = async (task: AutopilotTaskDto) => {
    if (!campaign) return;

    if (task.category?.taskType === TaskCategoryTaskType.AgreementDetails) {
      if (campaign.state === CampaignDetailModelState.NSW) await saveAgreementDetails();
      return;
    }

    if (task.category?.taskType === TaskCategoryTaskType.AgreementComplianceType) {
      if (campaign.state === CampaignDetailModelState.QLD) await saveAgreementDetails();
      if (campaign.state === CampaignDetailModelState.NSW) {
        await saveAgreementDetails();
        await saveCommissionDetails();
      }
      return;
    }

    if (
      task.category?.taskType === TaskCategoryTaskType.AgreementCommission &&
      campaign.state === CampaignDetailModelState.QLD
    ) {
      await saveCommissionDetails();
    }
  };

  const onSaveAllPages = async (modalTasks: AutopilotTaskDto[]) => {
    for (const task of modalTasks) {
      await onSaveCurrentPage(task);
    }
  };

  const renderPage = (props: TaskModalPageProps) => (
    <CompliancePages
      {...props}
      agreementDetails={agreementDetails}
      onAgreementDetailsUpdated={onAgreementDetailsUpdated}
      commissionDetails={commissionDetails}
      currentCommissionInfo={currentCommissionInfo}
      onCommissionUpdated={onCommissionUpdated}
      isAgreementTypeOpen={isAgreementTypeOpen(agreementDetails)}
      agreementExceededGap={hasExceededAgreementPeriod(agreementDetails)}
    />
  );

  const contentConfig: TaskModalContentConfig = {
    documents: filteredDocuments,
    isPending: documentsPending || agreementDetailsPending || commissionInformationPending,
    onSaveAllPages,
    renderPage,
    completeDisabled,
    flow: createAgreementComplianceTaskModalFlow(campaign.state),
  };

  return {
    campaign,
    contentConfig,
  };
};
